import { createContext, useCallback, useContext, useEffect, useId, useRef, useState, type ReactNode } from "react";
import { useBlocker } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";

type Section = { dirty: boolean; saving: boolean; save: () => unknown; discard: () => void };
type Registry = { sections: Map<string, Section>; set: (id: string, section: Section | null) => void };

const SaveBarContext = createContext<Registry | null>(null);

// Portal-wide: every inline form registers with useSaveBarSection, and one <SaveBar /> per page saves them all.
export function SaveBarProvider({ children }: { children: ReactNode }) {
  const [sections, setSections] = useState(() => new Map<string, Section>());
  const set = useCallback((id: string, section: Section | null) => {
    setSections((prev) => {
      const next = new Map(prev);
      if (section) next.set(id, section);
      else next.delete(id);
      return next;
    });
  }, []);
  const anyDirty = [...sections.values()].some((s) => s.dirty);

  return (
    <SaveBarContext.Provider value={{ sections, set }}>
      {children}
      <LeaveGuard active={anyDirty} />
    </SaveBarContext.Provider>
  );
}

// In-app navigation gets a confirm dialog; reload, close and external links get the browser's own prompt.
function LeaveGuard({ active }: { active: boolean }) {
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) => active && currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    if (!active) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [active]);

  return (
    <Dialog open={blocker.state === "blocked"} onOpenChange={(open) => !open && blocker.reset?.()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Leave without saving?</DialogTitle>
          <DialogDescription>You have unsaved changes on this page. They will be lost if you leave.</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => blocker.reset?.()}>
            Stay on page
          </Button>
          <Button variant="destructive" onClick={() => blocker.proceed?.()}>
            Leave without saving
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function useRegistry() {
  const registry = useContext(SaveBarContext);
  if (!registry) throw new Error("SaveBar needs a SaveBarProvider (PortalShell renders one).");
  return registry;
}

export function useSaveBarSection(section: Section) {
  const { set } = useRegistry();
  const id = useId();
  // save/discard close over fresh state each render; registering them by ref keeps the registry from churning.
  const latest = useRef(section);
  latest.current = section;
  const { dirty, saving } = section;

  useEffect(() => {
    set(id, { dirty, saving, save: () => latest.current.save(), discard: () => latest.current.discard() });
  }, [set, id, dirty, saving]);
  useEffect(() => () => set(id, null), [set, id]);
}

// Place as the last child of the page's content column: sticky, so it matches the column's width.
export function SaveBar() {
  const { sections } = useRegistry();
  const all = [...sections.values()];
  const dirty = all.filter((s) => s.dirty);
  const saving = all.some((s) => s.saving);
  if (dirty.length === 0 && !saving) return null;

  return (
    <div
      role="region"
      aria-label="Unsaved changes"
      className="sticky bottom-3 z-10 flex items-center justify-end gap-3 rounded-xl border bg-background/85 px-4 py-3 shadow-sm backdrop-blur"
    >
      <span className="mr-auto inline-flex items-center gap-1.5 text-xs font-medium text-amber-600 dark:text-amber-400">
        <span aria-hidden className="size-1.5 rounded-full bg-current" /> Unsaved changes
      </span>
      <Button variant="ghost" onClick={() => dirty.forEach((s) => s.discard())} disabled={saving}>
        Discard changes
      </Button>
      <Button onClick={() => dirty.forEach((s) => void s.save())} disabled={saving}>
        {saving && <Spinner className="size-4" />}
        Save changes
      </Button>
    </div>
  );
}
